import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useRole } from "./RoleContext";
import { useEntitlement } from "./EntitlementContext";
import { de, en, type TranslationDict } from "../i18n/translations";
import { authFetch } from "../utils/authFetch";
import { identityEmail } from "../utils/memberSession";

export type UserLanguage = "de" | "en";

type LanguageContextType = {
  language: UserLanguage;
  setLanguage: (language: UserLanguage) => void;
  /** Für den Mandanten freigeschaltete Sprachen – Auswahl in den Einstellungen. */
  availableLanguages: UserLanguage[];
  t: TranslationDict;
};

export const LANGUAGE_LABELS: Record<UserLanguage, { native: string; german: string }> = {
  de: { native: "Deutsch", german: "Deutsch" },
  en: { native: "English", german: "Englisch" },
};

const DICTS: Record<UserLanguage, TranslationDict> = { de, en };

const LanguageContext = createContext<LanguageContextType>({
  language: "de",
  setLanguage: () => {},
  availableLanguages: ["de", "en"],
  t: de,
});

/**
 * Im GA-Portal freigeschaltete Oberflächensprachen (`features.languages`).
 * Fehlt das Feld oder ist es leer, gelten beide – Bestandsmandanten verhalten
 * sich damit wie vor der Einführung.
 */
export function readTenantLanguages(features: { languages?: unknown } | null | undefined): UserLanguage[] {
  const both: UserLanguage[] = ["de", "en"];
  if (!features || typeof features !== "object") return both;
  const raw = features.languages;
  if (!Array.isArray(raw)) return both;
  const allowed = Array.from(new Set(raw.filter(isUserLanguage)));
  return allowed.length > 0 ? allowed : both;
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeTenantId(value: string): string {
  return value.trim().toLowerCase();
}

function isUserLanguage(value: unknown): value is UserLanguage {
  return value === "de" || value === "en";
}

function getLanguageStorageKey(tenantId: string, userEmail: string): string | null {
  const normalizedTenantId = normalizeTenantId(tenantId);
  const normalizedEmail = normalizeEmail(userEmail);
  if (!normalizedTenantId || normalizedTenantId === "default" || !normalizedEmail) return null;
  return `sherloxaware_language_${normalizedTenantId}_${normalizedEmail}`;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { tenantId } = useRole();
  const { entitlement } = useEntitlement();
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0];
  const userEmail = identityEmail(account?.username);
  const storageKey = getLanguageStorageKey(tenantId, userEmail);
  const [language, setLanguageState] = useState<UserLanguage>("de");

  useEffect(() => {
    if (!storageKey) {
      setLanguageState("de");
      return;
    }
    try {
      const saved = localStorage.getItem(storageKey);
      setLanguageState(isUserLanguage(saved) ? saved : "de");
    } catch {
      setLanguageState("de");
    }
  }, [storageKey]);

  // Der localStorage-Wert rendert sofort, ist aber gerätegebunden. Die im Profil gespeicherte
  // Sprache ist die Quelle der Wahrheit – sie gilt auf jedem Gerät und steuert die
  // automatischen Mails (der Cron kann localStorage nicht lesen).
  useEffect(() => {
    if (!storageKey || !tenantId || !userEmail) return;
    let cancelled = false;
    authFetch(`/api/user-profiles?tenantId=${encodeURIComponent(tenantId)}&email=${encodeURIComponent(userEmail)}`)
      .then(r => r.ok ? r.json() : null)
      .then((profile: { language?: string } | null) => {
        const stored = profile?.language ?? null;
        if (cancelled || !isUserLanguage(stored)) return;
        setLanguageState(stored);
        try {
          localStorage.setItem(storageKey, stored);
        } catch {}
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [storageKey, tenantId, userEmail]);

  const setLanguage = useCallback((nextLanguage: UserLanguage) => {
    if (!readTenantLanguages(entitlement?.features ?? null).includes(nextLanguage)) return;
    setLanguageState(nextLanguage);
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, nextLanguage);
    } catch {}
    if (!tenantId || !userEmail) return;
    void authFetch("/api/user-profiles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, email: userEmail, language: nextLanguage }),
    }).catch(() => {});
  }, [storageKey, tenantId, userEmail, entitlement]);

  // Nur freigeschaltete Sprachen zählen. Nimmt der Mandant Englisch zurück,
  // fällt eine bereits gewählte englische Oberfläche sofort auf Deutsch —
  // ohne dass jede Seite das selbst prüfen müsste.
  const availableLanguages = useMemo(
    () => readTenantLanguages(entitlement?.features ?? null),
    [entitlement],
  );
  const effectiveLanguage = availableLanguages.includes(language) ? language : availableLanguages[0];

  const value = useMemo<LanguageContextType>(() => ({
    language: effectiveLanguage,
    setLanguage,
    availableLanguages,
    t: DICTS[effectiveLanguage],
  }), [effectiveLanguage, setLanguage, availableLanguages]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function useT(): TranslationDict {
  return useContext(LanguageContext).t;
}

export function formatTemplate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : `{${key}}`
  );
}
